/*
  Search (spec chapter 15, update 2 chapter 2; architecture, "חיפוש"): one index
  in the browser serves the suggestions panel and the results page. It reads
  the catalog from /collections/catalog?view=search-index and the vocabulary
  from search-dictionary.json (catalog file, "מילון החיפוש").

  Matching rules from the catalog file:
  - a word matches from its start, from the second character ("valen" finds
    Valentino, "lent" finds nothing); no fuzzy matching;
  - case, geresh, hyphen and accents are ignored ("frederic", "זקט");
  - several words must all match ("תיק שחור" = black bags only);
  - order: brand or product name matches first, then type, category, color or
    material; within each, the catalog's "recommended" order.
*/
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const MAIN_CATEGORIES = ['בגדים', 'תיקים', 'נעליים', 'אביזרים', 'ביוטי'];

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/['׳״"`’\-]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  const wordsOf = (value) => normalize(value).split(/[^\p{L}\p{N}&]+/u).filter(Boolean);

  /* -------------------------------------------------------------------------
     The index
  ------------------------------------------------------------------------- */
  let loading = null;

  const build = (products, dictionary) => {
    products.forEach((product, order) => {
      product.order = order;
      product.nameTokens = new Set([...wordsOf(product.t), ...wordsOf(product.v), ...wordsOf(product.vh)]);
      product.otherTokens = new Set([
        ...wordsOf(product.ty),
        ...wordsOf(product.ct),
        ...wordsOf(product.colhe),
        ...wordsOf(product.col),
        ...wordsOf(product.m),
      ]);
    });

    const groups = [];
    const addGroup = (group) => {
      group.terms = [...new Set(group.terms.map(normalize).filter(Boolean))];
      group.termWords = group.terms.flatMap((term) => term.split(' '));
      groups.push(group);
    };

    const vendors = [...new Set(products.map((product) => product.v))];
    vendors.forEach((vendor) => {
      const sample = products.find((product) => product.v === vendor);
      addGroup({
        kind: 'brand',
        label: vendor,
        url: sample.vu,
        name: true,
        terms: [vendor, sample.vh, ...(dictionary.brands[vendor] || [])],
        test: (product) => product.v === vendor,
      });
    });

    Object.entries(dictionary.categories).forEach(([title, terms]) => {
      const sample = products.find((product) => product.ct === title);
      if (!sample) return;
      addGroup({ kind: 'category', label: title, url: sample.cu, terms: [title, ...terms], test: (product) => product.ct === title });
    });

    const types = [...new Set(products.map((product) => product.ty).filter(Boolean))];
    types.forEach((type) => {
      const sample = products.find((product) => product.ty === type);
      addGroup({
        kind: 'category',
        label: type,
        url: sample.tyu || sample.cu,
        terms: [type, ...(dictionary.types[type] || [])],
        test: (product) => product.ty === type,
      });
    });

    Object.entries(dictionary.colors).forEach(([color, terms]) => {
      addGroup({ kind: 'color', terms: [color, ...terms], test: (product) => product.col.split(' ').includes(color) });
    });

    dictionary.materials.forEach((terms) => {
      const normalized = terms.map(normalize);
      addGroup({
        kind: 'material',
        terms,
        test: (product) => normalized.some((term) => product.otherTokens.has(term) || product.nameTokens.has(term)),
      });
    });

    return {
      products,
      groups,
      byHandle: new Map(products.map((product) => [product.h, product])),
      // The color filter follows the catalog table's order.
      colorOrder: Object.keys(dictionary.colors),
    };
  };

  const load = (dictionaryUrl) => {
    if (!loading) {
      loading = Promise.all([
        fetch(`${root}collections/catalog?view=search-index`).then((res) => {
          if (!res.ok) throw new Error('index');
          return res.json();
        }),
        fetch(dictionaryUrl).then((res) => res.json()),
      ]).then(([feed, dictionary]) => build(feed.products, dictionary));
      loading.catch(() => {
        loading = null;
      });
    }
    return loading;
  };

  /** Returns { products, brands, categories } for a query of two characters or more. */
  const search = (index, query) => {
    const empty = { products: [], brands: [], categories: [] };
    const normalized = normalize(query);
    if (normalized.length < 2) return empty;
    const queryWords = normalized.split(' ');

    // A whole multi-word query can match one phrase, such as "תיק יד" or "valentino garavani".
    const phraseGroups =
      queryWords.length > 1 ? index.groups.filter((group) => group.terms.some((term) => term.startsWith(normalized))) : [];

    const matchedGroups = new Set(phraseGroups);
    const wordGroups = queryWords.map((word) => {
      const found = index.groups.filter((group) => group.termWords.some((term) => term.startsWith(word)));
      found.forEach((group) => matchedGroups.add(group));
      return found;
    });

    const results = [];
    index.products.forEach((product) => {
      let rank = 1;
      let matched;
      if (phraseGroups.length && phraseGroups.some((group) => group.test(product))) {
        matched = true;
        if (phraseGroups.some((group) => group.name && group.test(product))) rank = 0;
      } else {
        matched = queryWords.every((word, i) => {
          if ([...product.nameTokens].some((token) => token.startsWith(word))) {
            rank = 0;
            return true;
          }
          if ([...product.otherTokens].some((token) => token.startsWith(word))) return true;
          return wordGroups[i].some((group) => {
            if (!group.test(product)) return false;
            if (group.name) rank = 0;
            return true;
          });
        });
      }
      if (matched) results.push({ product, rank });
    });

    results.sort((a, b) => a.rank - b.rank || a.product.order - b.product.order);
    const found = results.map((result) => result.product);
    const hasProducts = (group) => found.some((product) => group.test(product));
    return {
      products: found,
      brands: [...matchedGroups].filter((group) => group.kind === 'brand' && hasProducts(group)),
      categories: [...matchedGroups].filter((group) => group.kind === 'category' && hasProducts(group)),
    };
  };

  /* -------------------------------------------------------------------------
     Small DOM helpers
  ------------------------------------------------------------------------- */
  const el = (tag, attributes = {}, children = []) => {
    const node = document.createElement(tag);
    Object.entries(attributes).forEach(([key, value]) => {
      if (value === undefined || value === null || value === false) return;
      if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value === true ? '' : value);
    });
    [].concat(children).forEach((child) => child && node.append(child));
    return node;
  };

  const pill = (label, url) => el('a', { class: 'pill', href: url || '#' }, el('bdi', { text: label }));

  const miniCard = (product, strings) =>
    el('li', { class: 'search-item' }, [
      el('a', { class: 'search-item__link', href: product.u }, [
        el('span', { class: 'search-item__media' }, product.img ? el('img', { src: product.img, alt: '', loading: 'lazy', width: 300, height: 400 }) : null),
        el('span', { class: 'label search-item__brand' }, el('bdi', { text: product.v })),
        el('span', { class: 'search-item__title' }, el('bdi', { text: product.t })),
        el('span', { class: 'price search-item__price', text: product.pt }),
      ]),
      el('button', {
        type: 'button',
        class: 'icon-button icon-button--solid search-item__heart card__heart',
        'aria-pressed': 'false',
        'aria-label': strings.wishlistAdd.replace('__TITLE__', product.t),
        'data-wishlist-toggle': String(product.id),
        'data-label-add': strings.wishlistAdd.replace('__TITLE__', product.t),
        'data-label-remove': strings.wishlistRemove.replace('__TITLE__', product.t),
      }, strings.heartIcon.cloneNode(true)),
      el('button', {
        type: 'button',
        class: 'icon-button icon-button--solid search-item__plus',
        'aria-label': strings.quickAdd.replace('__TITLE__', product.t),
        'data-quick-add': product.h,
        'data-fallback-url': product.u,
      }, strings.plusIcon.cloneNode(true)),
    ]);

  const syncHearts = () => document.dispatchEvent(new CustomEvent('demo-store:change', { detail: { key: 'wishlist' } }));

  /* -------------------------------------------------------------------------
     <amor-search>: the suggestions panel. Desktop: under the header, opened
     from the header field. Phone: full screen with its own field, opened from
     the search icon. Enter always goes to the results page.
  ------------------------------------------------------------------------- */
  class AmorSearch extends HTMLElement {
    connectedCallback() {
      this.headerInput = document.querySelector('[data-search-form] [data-search-input]');
      this.panelInput = this.querySelector('[data-search-panel-input]');
      this.idle = this.querySelector('[data-search-idle]');
      this.results = this.querySelector('[data-search-results]');
      this.empty = this.querySelector('[data-search-empty]');
      this.strings = {
        // The labels are data-label-*: a click anywhere in the panel bubbles up to the
        // document, which treats any [data-quick-add] ancestor as a card's plus (stage א8, note 4).
        wishlistAdd: this.dataset.labelWishlistAdd,
        wishlistRemove: this.dataset.labelWishlistRemove,
        quickAdd: this.dataset.labelQuickAdd,
        heartIcon: this.querySelector('[data-icon-heart] svg'),
        plusIcon: this.querySelector('[data-icon-plus] svg'),
      };

      this.headerInput?.addEventListener('focus', () => this.open());
      this.headerInput?.addEventListener('input', () => this.run(this.headerInput.value));
      this.panelInput?.addEventListener('input', () => this.run(this.panelInput.value));
      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-search-open]')) {
          event.preventDefault();
          this.open(true);
        }
      });
      this.querySelectorAll('[data-search-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.querySelector('[data-search-backdrop]')?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.hidden) this.close();
      });
      this.addEventListener('click', (event) => {
        const suggestion = event.target.closest('[data-search-term]');
        if (!suggestion) return;
        event.preventDefault();
        const input = this.activeInput();
        input.value = suggestion.dataset.searchTerm;
        input.focus();
        this.run(input.value);
      });
    }

    activeInput() {
      return this.fullScreen ? this.panelInput : this.headerInput || this.panelInput;
    }

    async open(fullScreen = false) {
      this.fullScreen = fullScreen || !window.matchMedia('(min-width: 860px)').matches;
      this.classList.toggle('search-panel--full', this.fullScreen);
      this.hidden = false;
      document.querySelector('amor-header')?.classList.add('is-solid');
      if (this.fullScreen) {
        document.documentElement.classList.add('is-locked');
        this.panelInput?.focus();
      }
      try {
        this.index = await load(this.dataset.dictionaryUrl);
        this.renderIdle();
        this.run(this.activeInput()?.value || '');
      } catch {
        // The suggestions failed to load: Enter still goes to the results page.
        this.idle.hidden = true;
      }
    }

    close() {
      this.hidden = true;
      document.documentElement.classList.remove('is-locked');
      document.querySelector('amor-header')?.classList.remove('is-solid');
    }

    renderIdle() {
      if (this.idleReady || !this.index) return;
      this.idleReady = true;
      const popular = this.idle.querySelector('[data-search-popular]');
      const brands = this.index.groups.filter((group) => group.kind === 'brand').map((group) => group.label).sort().slice(0, 6);
      // Popular searches: the brands and the main categories (spec chapter 15).
      const mainCategories = this.index.groups
        .filter((group) => group.kind === 'category' && MAIN_CATEGORIES.includes(group.label))
        .map((group) => group.label);
      [...brands, ...mainCategories].forEach((term) => {
        popular.append(el('button', { type: 'button', class: 'pill', 'data-search-term': term }, el('bdi', { text: term })));
      });
      popular.closest('[data-search-popular-block]').hidden = popular.children.length === 0;
      const recent = (window.DemoStore?.recentlyViewed() || []).map((handle) => this.index.byHandle.get(handle)).filter(Boolean).slice(0, 4);
      const recentList = this.idle.querySelector('[data-search-recent]');
      recentList.replaceChildren(...recent.map((product) => miniCard(product, this.strings)));
      recentList.closest('[data-search-recent-block]').hidden = recent.length === 0;
      syncHearts();
    }

    run(query) {
      if (!this.index) return;
      const result = search(this.index, query);
      const typing = normalize(query).length >= 2;
      this.idle.hidden = typing;
      this.results.hidden = !typing || result.products.length === 0;
      this.empty.hidden = !typing || result.products.length > 0;
      if (!typing) return;

      const brandBlock = this.results.querySelector('[data-search-brands]');
      brandBlock.querySelector('.pill-row').replaceChildren(...result.brands.map((group) => pill(group.label, group.url)));
      brandBlock.hidden = result.brands.length === 0;

      const categoryBlock = this.results.querySelector('[data-search-categories]');
      categoryBlock.querySelector('.pill-row').replaceChildren(...result.categories.slice(0, 8).map((group) => pill(group.label, group.url)));
      categoryBlock.hidden = result.categories.length === 0;

      this.results.querySelector('[data-search-list]').replaceChildren(
        ...result.products.slice(0, 6).map((product) => miniCard(product, this.strings))
      );
      const all = this.results.querySelector('[data-search-all]');
      all.href = `${root}search?q=${encodeURIComponent(query.trim())}`;
      all.textContent = this.dataset.allLabel.replace('__COUNT__', result.products.length);
      syncHearts();
    }
  }
  customElements.define('amor-search', AmorSearch);

  /* -------------------------------------------------------------------------
     <amor-search-results>: the results page. Every catalog card is rendered
     by Liquid; the index decides which show, in what order. Filter, sort and
     the grid view switch work as on the category page, but in the browser:
     the filter drawer offers the designers, categories, colors and sizes of
     the matches, with live counts, and applies on "show N results". Several
     values in one group widen the choice; groups narrow each other. The
     choices are kept in the page's history entry, so "back" restores them.
  ------------------------------------------------------------------------- */
  const LIST_KEYS = ['vendor', 'type', 'color', 'size'];
  const SIZE_SYSTEMS = ['IT', 'FR', 'EU'];
  const emptyFilters = () => ({ vendor: [], type: [], color: [], size: [], price: '', stock: false });
  const valueTests = {
    vendor: (product, values) => values.includes(product.v),
    type: (product, values) => values.includes(product.ty),
    color: (product, values) => product.col.split(' ').some((color) => values.includes(color)),
    size: (product, values) => (product.s || []).some((size) => values.includes(size)),
  };
  const sizeNumber = (size) => parseFloat(size.split(' ').pop()) || 0;
  const bySize = (a, b) => {
    const system = (size) => {
      const i = SIZE_SYSTEMS.indexOf(size.split(' ')[0]);
      return i === -1 ? SIZE_SYSTEMS.length : i;
    };
    return system(a) - system(b) || sizeNumber(a) - sizeNumber(b) || a.localeCompare(b);
  };

  class AmorSearchResults extends HTMLElement {
    async connectedCallback() {
      this.query = this.dataset.query || '';
      this.grid = this.querySelector('[data-search-grid]');
      this.form = this.querySelector('[data-filter-form]');
      this.items = new Map([...this.grid.querySelectorAll('[data-handle]')].map((item) => [item.dataset.handle, item]));
      this.sortValue = 'manual';
      this.applied = emptyFilters();

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-sort]')) this.sort(event.target.value);
        else if (event.target.closest('[data-filter-form]')) this.onFilterChange(event);
      });
      this.addEventListener('click', (event) => this.onClick(event));
      // Choices that were never applied are dropped when the drawer opens again.
      this.addEventListener('drawer:open', () => {
        this.setForm(this.applied);
        this.updateDrawer();
      });
      this.syncView();

      try {
        this.index = await load(this.dataset.dictionaryUrl);
      } catch {
        // No index: Shopify's own results, rendered by Liquid, stay visible.
        this.querySelector('[data-search-fallback]')?.removeAttribute('hidden');
        this.querySelector('[data-search-main]')?.setAttribute('hidden', '');
        return;
      }
      this.matches = search(this.index, this.query).products;
      this.buildOptions();
      const saved = window.history.state?.amorSearch;
      if (saved && saved.query === this.query) {
        this.sortValue = saved.sort || 'manual';
        this.applied = { ...emptyFilters(), ...saved.filters };
        const select = this.querySelector('[data-sort]');
        if (select) select.value = this.sortValue;
        this.syncSortLabel();
      }
      this.setForm(this.applied);
      this.show();
    }

    onClick(event) {
      const view = event.target.closest('[data-view]');
      if (view) {
        window.DemoStore?.setGridView(view.dataset.view);
        this.syncView();
        return;
      }
      if (event.target.closest('[data-filter-clear]')) {
        this.setForm(emptyFilters());
        this.updateDrawer();
        return;
      }
      if (event.target.closest('[data-filter-apply]')) {
        this.apply(this.readForm());
        this.querySelector('amor-drawer')?.close();
        return;
      }
      if (event.target.closest('[data-filter-reset]')) {
        this.apply(emptyFilters());
        return;
      }
      const remove = event.target.closest('[data-filter-remove]');
      if (remove) this.apply(this.without(remove.dataset.filterRemove));
    }

    onFilterChange(event) {
      // The four price pills are one choice: picking one clears the others.
      if (event.target.matches('[data-price-range]') && event.target.checked) {
        this.form.querySelectorAll('[data-price-range]').forEach((input) => input !== event.target && (input.checked = false));
      }
      this.updateDrawer();
    }

    syncView() {
      const current = window.DemoStore?.gridView() || 'default';
      this.querySelectorAll('[data-view]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.view === current)));
    }

    syncSortLabel() {
      const label = this.querySelector('[data-sort-value]');
      const option = this.querySelector(`[data-sort] option[value="${this.sortValue}"]`);
      if (label && option) label.textContent = option.textContent;
    }

    /* --- Filtering ---------------------------------------------------------- */

    inPrice(product, range) {
      const input = this.form?.querySelector(`[data-price-range="${range}"]`);
      if (!input) return true;
      const from = Number(input.dataset.gte || 0);
      const to = input.dataset.lte ? Number(input.dataset.lte) : Infinity;
      return product.p >= from && product.p <= to;
    }

    /** Whether product passes every chosen filter, leaving out the group except. */
    passes(product, filters, except) {
      for (const key of LIST_KEYS) {
        if (key !== except && filters[key].length && !valueTests[key](product, filters[key])) return false;
      }
      if (except !== 'price' && filters.price && !this.inPrice(product, filters.price)) return false;
      if (except !== 'stock' && filters.stock && !product.a) return false;
      return true;
    }

    filterCount(filters) {
      return LIST_KEYS.reduce((sum, key) => sum + filters[key].length, 0) + (filters.price ? 1 : 0) + (filters.stock ? 1 : 0);
    }

    plural(prefix, count) {
      if (count === 0 && this.dataset[`${prefix}Zero`]) return this.dataset[`${prefix}Zero`];
      if (count === 1) return this.dataset[`${prefix}One`];
      if (count === 2 && this.dataset[`${prefix}Two`]) return this.dataset[`${prefix}Two`];
      return this.dataset[`${prefix}Other`].replace('__COUNT__', count);
    }

    /** The drawer's options: only values that the matches have. */
    buildOptions() {
      if (!this.form) return;
      const distinct = (pick) => [...new Set(this.matches.flatMap(pick))].filter(Boolean);
      const values = {
        vendor: distinct((product) => [product.v]).sort((a, b) => a.localeCompare(b)),
        type: distinct((product) => [product.ty]).sort((a, b) => a.localeCompare(b, 'he')),
        color: distinct((product) => product.col.split(' ')).sort((a, b) => {
          const order = this.index.colorOrder;
          const rank = (color) => (order.includes(color) ? order.indexOf(color) : order.length);
          return rank(a) - rank(b) || a.localeCompare(b);
        }),
        size: distinct((product) => product.s || []).sort(bySize),
      };
      const swatches = this.querySelector('[data-swatch-template]')?.content;

      const option = (key, value) => {
        const isSize = key === 'size';
        const label = el('label', { class: isSize ? 'pill filter-pill' : 'filter-option', 'data-filter-value': '' }, [
          el('input', { class: 'visually-hidden', type: 'checkbox', name: key, value }),
        ]);
        if (key === 'color') {
          const swatch = [...(swatches?.querySelectorAll('[data-swatch-for]') || [])].find((node) => node.dataset.swatchFor === value);
          label.append(swatch ? swatch.cloneNode(true) : el('span', { class: 'filter-option__box', 'aria-hidden': 'true' }));
        } else if (!isSize) {
          label.append(el('span', { class: 'filter-option__box', 'aria-hidden': 'true' }));
        }
        label.append(el('span', { dir: isSize ? 'ltr' : 'auto', text: value }), el('span', { class: 'filter-count', 'data-option-count': '' }));
        return label;
      };

      LIST_KEYS.forEach((key) => {
        const container = this.form.querySelector(`[data-filter-options="${key}"]`);
        if (!container) return;
        if (key === 'size') {
          const systems = [...new Set(values.size.map((size) => size.split(' ')[0]))];
          container.replaceChildren(
            ...systems.map((system) =>
              el('div', { class: 'filter-sizes' }, [
                systems.length > 1 ? el('span', { class: 'label filter-sizes__system' }, el('bdi', { text: system })) : null,
                el('div', { class: 'pill-row' }, values.size.filter((size) => size.split(' ')[0] === system).map((size) => option(key, size))),
              ])
            )
          );
        } else {
          container.replaceChildren(...values[key].map((value) => option(key, value)));
        }
        // As on the category page, a group with one value has nothing to choose.
        container.closest('[data-filter-group]').hidden = values[key].length < 2;
      });
    }

    readForm() {
      const filters = emptyFilters();
      if (!this.form) return filters;
      this.form.querySelectorAll('input[name]:checked').forEach((input) => filters[input.name]?.push(input.value));
      filters.price = this.form.querySelector('[data-price-range]:checked')?.dataset.priceRange || '';
      filters.stock = Boolean(this.form.querySelector('[data-in-stock]')?.checked);
      return filters;
    }

    setForm(filters) {
      if (!this.form) return;
      this.form.querySelectorAll('input[name]').forEach((input) => {
        input.checked = (filters[input.name] || []).includes(input.value);
      });
      this.form.querySelectorAll('[data-price-range]').forEach((input) => {
        input.checked = input.dataset.priceRange === filters.price;
      });
      const stock = this.form.querySelector('[data-in-stock]');
      if (stock) stock.checked = filters.stock;
    }

    /** Live counts while choosing: each option, the price pills, in stock, and the "show" button. */
    updateDrawer() {
      if (!this.form || !this.matches) return;
      const pending = this.readForm();
      const setCount = (holder, input, count) => {
        holder.querySelector('[data-option-count]').textContent = String(count);
        const empty = count === 0 && !input.checked;
        holder.classList.toggle('is-empty', empty);
        if (input.getAttribute('role') !== 'switch') input.disabled = empty;
      };
      this.form.querySelectorAll('[data-filter-value]').forEach((holder) => {
        const input = holder.querySelector('input');
        const count = this.matches.filter(
          (product) => this.passes(product, pending, input.name) && valueTests[input.name](product, [input.value])
        ).length;
        setCount(holder, input, count);
      });
      this.form.querySelectorAll('[data-price-option]').forEach((holder) => {
        const input = holder.querySelector('[data-price-range]');
        const count = this.matches.filter(
          (product) => this.passes(product, pending, 'price') && this.inPrice(product, input.dataset.priceRange)
        ).length;
        setCount(holder, input, count);
      });
      const stock = this.form.querySelector('[data-stock-option]');
      if (stock) {
        const count = this.matches.filter((product) => this.passes(product, pending, 'stock') && product.a).length;
        setCount(stock, stock.querySelector('[data-in-stock]'), count);
      }
      const total = this.matches.filter((product) => this.passes(product, pending)).length;
      const apply = this.querySelector('[data-filter-apply]');
      if (apply) {
        apply.textContent = this.plural('show', total);
        apply.disabled = total === 0;
      }
    }

    without(token) {
      const [key, ...rest] = token.split(':');
      const value = rest.join(':');
      const filters = { ...this.applied, ...Object.fromEntries(LIST_KEYS.map((name) => [name, [...this.applied[name]]])) };
      if (key === 'price') filters.price = '';
      else if (key === 'stock') filters.stock = false;
      else filters[key] = filters[key].filter((item) => item !== value);
      return filters;
    }

    apply(filters) {
      this.applied = filters;
      this.setForm(filters);
      this.show();
      const status = this.querySelector('[data-search-status]');
      if (status) status.textContent = this.querySelector('[data-search-count]')?.textContent || '';
    }

    /* --- Showing ------------------------------------------------------------ */

    ordered() {
      const list = [...this.matches];
      if (this.sortValue === 'price-ascending') list.sort((a, b) => a.p - b.p);
      else if (this.sortValue === 'price-descending') list.sort((a, b) => b.p - a.p);
      else if (this.sortValue === 'created-descending') list.sort((a, b) => Number(b.n) - Number(a.n) || a.order - b.order);
      return list;
    }

    show() {
      const visible = this.ordered().filter((product) => this.passes(product, this.applied));
      this.render(visible);
      this.renderActive();
      window.history.replaceState(
        { ...(window.history.state || {}), amorSearch: { query: this.query, sort: this.sortValue, filters: this.applied } },
        ''
      );
    }

    render(products) {
      this.items.forEach((item) => {
        item.hidden = true;
      });
      products.forEach((product) => {
        const item = this.items.get(product.h);
        if (!item) return;
        item.hidden = false;
        this.grid.append(item);
      });
      const count = this.querySelector('[data-search-count]');
      if (count) {
        count.textContent =
          products.length === 1 ? this.dataset.countOne : this.dataset.countOther.replace('__COUNT__', products.length);
      }
      const found = this.matches.length > 0;
      this.querySelector('[data-search-none]').hidden = found;
      this.querySelector('[data-search-toolbar]').hidden = !found;
      this.querySelector('[data-filtered-empty]').hidden = !found || products.length > 0;
      this.grid.hidden = products.length === 0;
      this.setAttribute('aria-busy', 'false');
    }

    /** The removable pills under the toolbar, and the count on the filter button. */
    renderActive() {
      const container = this.querySelector('[data-active-filters]');
      const total = this.filterCount(this.applied);
      const badge = this.querySelector('[data-filter-count]');
      if (badge) {
        badge.replaceChildren(
          ...(total > 0
            ? [
                el('span', { class: 'toolbar-count', 'aria-hidden': 'true', text: String(total) }),
                el('span', { class: 'visually-hidden', text: this.plural('active', total) }),
              ]
            : [])
        );
      }
      if (!container) return;
      const closeIcon = this.querySelector('[data-drawer-close] svg');
      const pills = [];
      const addPill = (token, label) =>
        pills.push(
          el(
            'button',
            {
              type: 'button',
              class: 'pill pill--removable',
              'data-filter-remove': token,
              'aria-label': this.dataset.removeLabel.replace('__LABEL__', label),
            },
            [el('span', { dir: 'auto', text: label }), closeIcon ? closeIcon.cloneNode(true) : null]
          )
        );
      LIST_KEYS.forEach((key) => this.applied[key].forEach((value) => addPill(`${key}:${value}`, value)));
      if (this.applied.price) {
        const input = this.form?.querySelector(`[data-price-range="${this.applied.price}"]`);
        addPill('price:', input?.dataset.label || '');
      }
      if (this.applied.stock) addPill('stock:', this.form?.querySelector('[data-in-stock]')?.dataset.label || '');
      if (total > 1) {
        pills.push(el('button', { type: 'button', class: 'link active-filters__clear', 'data-filter-reset': '', text: this.dataset.clearLabel }));
      }
      container.replaceChildren(...pills);
      container.hidden = total === 0;
    }

    sort(value) {
      this.sortValue = value;
      this.syncSortLabel();
      if (this.matches) this.show();
    }
  }
  customElements.define('amor-search-results', AmorSearchResults);

  // The same index for other parts of the site (AMOR BY YOU sends brand and
  // category requests to a filtered page, update 2 chapter 8).
  window.AmorSearchIndex = { load, search, normalize };
})();
